import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  VisibilityOff as DraftIcon,
  Visibility as PublishIcon,
  ArrowBack as BackIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { createBlogPost, getBlogPostById, updateBlogPost, getCategories, getTags } from '../../services/blogService';

// Rich text editor (you can replace with your preferred editor)
import { Editor } from '@tinymce/tinymce-react';

const BlogPostForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    category: '',
    tags: [],
    author: '',
    featured: false,
    published: false,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  // Load data for editing
  useEffect(() => {
    const loadData = async () => {
      if (isEditing) {
        setLoading(true);
        try {
          const response = await getBlogPostById(id);
          if (response.success && response.data) {
            const post = response.data;
            setFormData({
              title: post.title || '',
              summary: post.summary || '',
              content: post.content || '',
              category: post.category || '',
              tags: post.tags || [],
              author: post.author || '',
              featured: post.featured || false,
              published: post.published || false,
            });
          }
        } catch (error) {
          setErrorMessage('Failed to load blog post data');
          console.error('Error loading blog post:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadData();
  }, [id, isEditing]);

  // Load categories and tags
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const [categoriesResponse, tagsResponse] = await Promise.all([
          getCategories(),
          getTags(),
        ]);
        
        if (categoriesResponse.success && categoriesResponse.data) {
          setCategories(categoriesResponse.data);
        }
        
        if (tagsResponse.success && tagsResponse.data) {
          setAvailableTags(tagsResponse.data);
        }
      } catch (error) {
        console.error('Error loading categories and tags:', error);
      }
    };

    loadMetadata();
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle rich text editor changes
  const handleEditorChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      content,
    }));
  };

  // Add a tag
  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  // Remove a tag
  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  // Handle tag input on key press
  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Save blog post
  const handleSave = async (publish = false) => {
    // Validate form data
    if (!formData.title.trim()) {
      setErrorMessage('Title is required');
      return;
    }

    if (!formData.content.trim()) {
      setErrorMessage('Content is required');
      return;
    }

    if (!formData.category.trim()) {
      setErrorMessage('Category is required');
      return;
    }

    setSaving(true);
    
    try {
      const postData = {
        ...formData,
        published: publish !== undefined ? publish : formData.published,
      };

      let response;
      if (isEditing) {
        response = await updateBlogPost(id, postData);
      } else {
        response = await createBlogPost(postData);
      }

      if (response.success) {
        setSuccessMessage(
          `Blog post ${isEditing ? 'updated' : 'created'} successfully!`
        );
        
        // Navigate back to list after saving new post or continue editing
        if (!isEditing) {
          setTimeout(() => {
            navigate('/blog');
          }, 1500);
        }
      } else {
        setErrorMessage(response.error || 'Failed to save blog post');
      }
    } catch (error) {
      setErrorMessage('Error saving blog post');
      console.error('Error saving blog post:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mr: 2 }}
        >
          Back to List
        </Button>
        <Typography variant="h4" component="h1">
          {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main content */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              variant="outlined"
              margin="normal"
              required
              error={errorMessage.includes('Title')}
              helperText={errorMessage.includes('Title') ? errorMessage : ''}
            />

            <TextField
              fullWidth
              label="Summary"
              name="summary"
              value={formData.summary}
              onChange={handleChange}
              variant="outlined"
              margin="normal"
              multiline
              rows={3}
              helperText="A brief description of the blog post for previews (optional)"
            />

            <Box sx={{ mt: 3, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Content
              </Typography>
              <Editor
                apiKey="your-tinymce-api-key" // Replace with your TinyMCE API key
                value={formData.content}
                init={{
                  height: 500,
                  menubar: true,
                  plugins: [
                    'advlist autolink lists link image charmap print preview anchor',
                    'searchreplace visualblocks code fullscreen',
                    'insertdatetime media table paste code help wordcount'
                  ],
                  toolbar:
                    'undo redo | formatselect | bold italic backcolor | \
                    alignleft aligncenter alignright alignjustify | \
                    bullist numlist outdent indent | removeformat | help'
                }}
                onEditorChange={handleEditorChange}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Post Settings */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Post Settings
            </Typography>
            
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                name="category"
                value={formData.category}
                onChange={handleChange}
                label="Category"
                error={errorMessage.includes('Category')}
              >
                <MenuItem value="">Select a category</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
              {errorMessage.includes('Category') && (
                <FormHelperText error>{errorMessage}</FormHelperText>
              )}
            </FormControl>

            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Add a tag"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleTagInputKeyPress}
                  sx={{ mr: 1 }}
                />
                <Button variant="outlined" onClick={handleAddTag}>
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Author"
              name="author"
              value={formData.author}
              onChange={handleChange}
              variant="outlined"
              margin="normal"
            />
            
            <Divider sx={{ my: 2 }} />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.featured}
                  onChange={handleChange}
                  name="featured"
                  color="primary"
                />
              }
              label="Feature this post"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.published}
                  onChange={handleChange}
                  name="published"
                  color="primary"
                />
              }
              label="Publish"
            />
          </Paper>

          {/* Action Buttons */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={() => handleSave()}
                disabled={saving}
                fullWidth
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
              
              <Button
                variant="outlined"
                color="secondary"
                startIcon={formData.published ? <DraftIcon /> : <PublishIcon />}
                onClick={() => handleSave(!formData.published)}
                disabled={saving}
                fullWidth
              >
                {formData.published ? 'Unpublish' : 'Publish'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<StarIcon />}
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    featured: !prev.featured,
                  }));
                }}
                fullWidth
              >
                {formData.featured ? 'Remove from Featured' : 'Add to Featured'}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Alerts */}
      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage('')} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BlogPostForm;